import React from 'react';
import { Box, IconButton, Tooltip, Typography, alpha, useTheme } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ChevronLeft as CollapseIcon,
  ChevronRight as ExpandIcon,
} from '@mui/icons-material';
import { getWorkbenchNavItems } from './workbenchNavItems';
import { useEnterpriseAuth } from '../context/EnterpriseAuthContext';

export default function ActivityBar({ width = 56 }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user, federationMode } = useEnterpriseAuth();

  // Determine ASC mode using same priority as App.jsx:
  // 1. Federation mode from JWT (highest priority)
  // 2. User's mode property
  // 3. localStorage appMode
  // 4. URL path starting with /asc
  const isASC = React.useMemo(() => {
    try {
      // Federation mode takes highest priority
      if (federationMode === 'asc') return true;
      if (federationMode === 'hospital') return false;

      // Check user mode
      if (user?.mode === 'asc') return true;

      // Check localStorage
      if (localStorage.getItem('appMode') === 'asc') return true;

      // Check URL path
      return pathname.startsWith('/asc');
    } catch {
      return pathname.startsWith('/asc');
    }
  }, [pathname, federationMode, user?.mode]);
  const [expanded, setExpanded] = React.useState(() => {
    try {
      return localStorage.getItem('wb_activity_expanded') === '1';
    } catch {
      return false;
    }
  });
  React.useEffect(() => {
    try {
      localStorage.setItem('wb_activity_expanded', expanded ? '1' : '0');
    } catch {}
    try {
      const w = expanded ? 200 : width;
      document.documentElement.style.setProperty('--wb-activity-width', `${w}px`);
      window.dispatchEvent(new CustomEvent('wb:activity-width', { detail: { width: w } }));
    } catch {}
  }, [expanded]);
  React.useEffect(() => {
    try {
      const w = expanded ? 200 : width;
      document.documentElement.style.setProperty('--wb-activity-width', `${w}px`);
    } catch {}
  }, [width]);

  return (
    <Box
      sx={{
        position: 'relative',
        width: expanded ? 200 : width,
        minWidth: expanded ? 200 : width,
        transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
        background:
          theme.palette.mode === 'dark'
            ? `linear-gradient(180deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${theme.palette.background.default} 100%)`
            : `linear-gradient(180deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
        borderRight: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: expanded ? 'stretch' : 'center',
        py: 1.5,
        gap: 0.25,
        boxShadow:
          theme.palette.mode === 'dark'
            ? `1px 0 8px ${alpha(theme.palette.common.black, 0.3)}`
            : `1px 0 8px ${alpha(theme.palette.primary.main, 0.05)}`,
      }}
    >
      {expanded && (
        <Box
          sx={{
            px: 2,
            pb: 1.5,
            mb: 0.5,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              color: 'primary.main',
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              fontSize: '0.7rem',
            }}
          >
            Navigation
          </Typography>
        </Box>
      )}
      {getWorkbenchNavItems(isASC).map(item => {
        const active = pathname === item.path || pathname.startsWith(`${item.path}/`);
        return expanded ? (
          <Box
            key={item.key}
            onClick={() => navigate(item.path)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              mx: 1,
              px: 1.5,
              py: 1,
              cursor: 'pointer',
              color: active ? 'primary.main' : 'text.secondary',
              bgcolor: active ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
              borderRadius: 2,
              borderLeft: active
                ? `3px solid ${theme.palette.primary.main}`
                : '3px solid transparent',
              transition: 'all 150ms ease',
              '&:hover': {
                bgcolor: active
                  ? alpha(theme.palette.primary.main, 0.12)
                  : alpha(theme.palette.action.hover, 0.8),
                transform: 'translateX(2px)',
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: active ? 'primary.main' : 'text.secondary',
              }}
            >
              {item.icon}
            </Box>
            <Typography
              variant="body2"
              noWrap
              sx={{
                fontWeight: active ? 600 : 500,
                fontSize: '0.85rem',
              }}
            >
              {item.label}
            </Typography>
          </Box>
        ) : (
          <Tooltip key={item.key} title={item.label} placement="right" arrow>
            <IconButton
              size="small"
              onClick={() => navigate(item.path)}
              sx={{
                width: 42,
                height: 42,
                borderRadius: 2,
                color: active ? 'primary.main' : 'text.secondary',
                bgcolor: active ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                transition: 'all 150ms ease',
                '&:hover': {
                  bgcolor: active
                    ? alpha(theme.palette.primary.main, 0.15)
                    : alpha(theme.palette.action.hover, 0.8),
                  transform: 'scale(1.05)',
                },
              }}
            >
              {item.icon}
            </IconButton>
          </Tooltip>
        );
      })}
      <Box sx={{ flex: 1 }} />
      <Tooltip title={expanded ? 'Collapse sidebar' : 'Expand sidebar'} placement="right" arrow>
        <IconButton
          size="small"
          onClick={() => setExpanded(v => !v)}
          sx={{
            position: expanded ? 'absolute' : 'relative',
            right: expanded ? -14 : 'auto',
            bottom: expanded ? 20 : 'auto',
            width: 28,
            height: 28,
            bgcolor: theme.palette.background.paper,
            border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
            borderRadius: '50%',
            boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.15)}`,
            color: 'text.secondary',
            zIndex: 10,
            transition: 'all 200ms ease',
            '&:hover': {
              bgcolor: theme.palette.primary.main,
              color: 'white',
              borderColor: theme.palette.primary.main,
              transform: 'scale(1.1)',
            },
          }}
        >
          {expanded ? <CollapseIcon sx={{ fontSize: 18 }} /> : <ExpandIcon sx={{ fontSize: 18 }} />}
        </IconButton>
      </Tooltip>
    </Box>
  );
}
